import { useEffect, useState } from 'react'
import { audienceAPI } from '../../services/api'
import type { AudienceInsights, ConversionStats } from '../../types'

/**
 * Monetization → Overview: what the revenue charts don't show. How many
 * followers became subscribers, trials, this month's sign-ups and endings,
 * how many stayed, how long ended subscriptions lasted, and subscribers by
 * tier. No lifetime-value estimate: the one available multiplies by how long
 * current subscribers have stayed so far, which isn't a lifetime.
 */
const AudiencePanel = () => {
  const [insights, setInsights] = useState<AudienceInsights | null>(null)
  const [conversion, setConversion] = useState<ConversionStats | null>(null)

  useEffect(() => {
    let stopped = false
    // Optional: the panel shows what loads.
    Promise.resolve()
      .then(() => audienceAPI.insights())
      .then((i) => !stopped && setInsights(i))
      .catch(() => undefined)
    Promise.resolve()
      .then(() => audienceAPI.conversion())
      .then((c) => !stopped && setConversion(c))
      .catch(() => undefined)
    return () => {
      stopped = true
    }
  }, [])

  if (!insights && !conversion) return null

  const rows: [string, string][] = []
  if (insights) {
    rows.push(['Followers', insights.total_followers.toLocaleString()])
    rows.push([
      'Subscribers',
      insights.total_followers > 0
        ? `${insights.total_subscribers.toLocaleString()} (${insights.follower_to_subscriber_rate_pct}% of followers)`
        : insights.total_subscribers.toLocaleString(),
    ])
  }
  if (conversion) {
    if (conversion.trialing_subscribers > 0) rows.push(['On a trial', String(conversion.trialing_subscribers)])
    rows.push(['Last 30 days', `+${conversion.new_this_month} new, −${conversion.churned_this_month} ended`])
    if (conversion.retention_rate_pct !== null) rows.push(['Still subscribed', `${conversion.retention_rate_pct}% of everyone who subscribed`])
    if (conversion.avg_lifetime_days !== null)
      rows.push(['Ended subscriptions lasted', `${Math.round(conversion.avg_lifetime_days)} days on average`])
  }
  const tiers = Object.entries(insights?.tier_distribution ?? {}).sort((a, b) => b[1] - a[1])

  return (
    <section className="mt-6 rounded-lg bg-white p-6 shadow" aria-label="Audience">
      <h3 className="text-lg font-bold text-gray-900">Audience</h3>
      <dl className="mt-3 space-y-2 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4">
            <dt className="text-gray-600">{label}</dt>
            <dd className="text-right font-medium text-gray-900">{value}</dd>
          </div>
        ))}
      </dl>
      {tiers.length > 0 && (
        <div className="mt-4 border-t border-gray-200 pt-3">
          <h4 className="text-sm font-medium text-gray-700">Subscribers by tier</h4>
          <ul className="mt-1 space-y-1 text-sm">
            {tiers.map(([name, count]) => (
              <li key={name} className="flex justify-between">
                <span className="text-gray-600">{name}</span>
                <span className="font-medium text-gray-900">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

export default AudiencePanel
